import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { fetchPublicMindItem as fetchPublicMindItemAction } from '../actions/mindItems.actions';
import {
  selectPublicMindItem,
  isPublicMindItemInvalid as getMindItemInvalidity,
} from '../reducers/mindItems.selectors';
import { ReaderContext } from '../components/Reader/ReaderContext';
import { amplitudeContentTypes, amplitudeEvents, routes } from '../constants';
import PublicVideoPlayerLayout from '../components/AppLayout/PublicVideoPlayerLayout';
import { setCustomPageTitle } from '../utils/customTitle';
import useRouter from '../utils/useRouter';
import useAction from '../utils/useAction';
import { noop } from 'lodash';
import { AmplitudeTrackPayload } from '@klab-berlin/api-sdk/lib/types/requests/Track';
import { getContentEventPayload, waitForAmpliReady } from '../utils/ampli';
import { ampliServerSideEventTrack } from '../services/track.service';
import { notifyBugsnagHandledError } from '../utils/bugsnagClient';
import NewReader from '../components/NewReader/NewReader';

const PublicVideoPlayerPage: React.FC = () => {
  const { match: { params: { accessToken } }, router } = useRouter();
  const fetchPublicMindItem = useAction(fetchPublicMindItemAction);
  const mindItem = useSelector(state => selectPublicMindItem(state, accessToken));
  const isMindItemInvalid = useSelector(state => getMindItemInvalidity(state, accessToken));
  const [isReaderDetailsCollapsed, setIsReaderDetailsCollapsed] = useState(false);

  useEffect(() => {
    fetchPublicMindItem(accessToken);
  }, [accessToken]);

  const trackedMindItemIdRef = useRef<string>();

  useEffect(() => {
    if (mindItem) {
      setCustomPageTitle(mindItem.title);

      if (trackedMindItemIdRef.current !== mindItem.id) {
        trackedMindItemIdRef.current = mindItem.id;

        (async () => {
          await waitForAmpliReady();

          const contentViewedPayload: AmplitudeTrackPayload | null = getContentEventPayload({
            content: {
              ...mindItem,
              publicVideo: 'Yes'
            },
            event_type: amplitudeEvents.ContentViewed,
            contentType: amplitudeContentTypes.video
          });
          if (contentViewedPayload) {
            ampliServerSideEventTrack(true, contentViewedPayload);
          }
        })().catch(notifyBugsnagHandledError);
      }
    }
  }, [mindItem?.id]);

  if (isMindItemInvalid) {
    router.push(`/${routes.notFound.root}`);
  }
  if (!mindItem || mindItem?.fileType !== 'video') {
    return <></>;
  }

  return (
    <ReaderContext.Provider
      value={{
        currentPage: 0,
        setInitialPage: noop,
        setCurrentPage: noop,
        initialPage: 0,
        mindItemId: mindItem.id,
        isReadOnly: mindItem.isReadOnly,
        nextSibling: undefined,
        isPinned: mindItem.isPinned,
        isInCollection: false,
        isTextBased: mindItem.isTextBased,
        onPageLoad: noop,
        pagesLoaded: true,
        hasAttachments: mindItem.hasAttachments,
        showNewReader: true,
        isReaderDetailsCollapsed: isReaderDetailsCollapsed,
        setReaderDetailsCollapsed: setIsReaderDetailsCollapsed,
        zoomProperties: {
          zoomFactor: 1,
          setZoomFactor: noop,
          baselineZoomFactor: 1,
          setBaselineZoomFactor: noop,
        },
        selectedPagesForAI: [],
        setSelectedPagesForAI: noop,
      }}
    >
      <PublicVideoPlayerLayout videoTitle={mindItem.title}>
        <NewReader isPublicVideoPlayer={true} />
      </PublicVideoPlayerLayout>
    </ReaderContext.Provider>
  );
};

export default PublicVideoPlayerPage;
